import { Link } from "react-router-dom";
import { Shield, Mail, FileText, HelpCircle, Building2 } from "lucide-react";
import { SEO } from "../components/SEO";

export const LegalPage = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12" data-testid="legal-page">
    <SEO
      title="Privacy & Voorwaarden — Debate Engine"
      description="Privacybeleid, gebruiksvoorwaarden, KvK-gegevens en juridische informatie van Debate Engine (BoPeDo). GDPR-conform."
      path="/legal"
    />
    <h1 className="text-2xl font-black mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>Juridisch & Informatie</h1>

    {/* Company Info */}
    <section className="mb-8 bg-white border-2 border-gray-200 p-6" data-testid="company-info">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Building2 className="w-5 h-5" />Bedrijfsinformatie</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div><span className="text-gray-500 block text-xs font-mono uppercase">Bedrijf</span>BoPeDo</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">Merk/Project</span>An RBG-LLM project</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">KvK</span>92541488</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">BTW</span>127621052B02</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">Land</span>Nederland</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">Vestiging</span>Slangenburg 8, 7608RR Almelo</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">Contact</span>info@debate-engine.com</div>
        <div><span className="text-gray-500 block text-xs font-mono uppercase">Founder</span>Ronald Lubach</div>
      </div>
    </section>

    {/* Privacy Policy */}
    <section className="mb-8 bg-white border-2 border-gray-200 p-6" data-testid="privacy-policy">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Shield className="w-5 h-5" />Privacybeleid</h2>
      <div className="text-sm text-gray-700 space-y-3">
        <p><strong>Welke gegevens verzamelen wij?</strong></p>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Discussievragen en antwoorden (voor kwaliteitsverbetering)</li>
          <li>Feedbackscores (anoniem)</li>
          <li>Betalingsgegevens (via Stripe, wij slaan geen kaartgegevens op)</li>
        </ul>
        <p><strong>AVG/GDPR Compliance</strong></p>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Gegevens worden maximaal 90 dagen bewaard zonder expliciete toestemming</li>
          <li>Recht op verwijdering: mail naar info@bopedo.nl</li>
          <li>Geen verkoop van persoonlijke gegevens aan derden</li>
          <li>Data opgeslagen binnen de EU</li>
        </ul>
        <p><strong>Cookies</strong></p>
        <p className="text-gray-600">We gebruiken alleen technisch noodzakelijke cookies. Geen tracking cookies zonder toestemming.</p>
      </div>
    </section>

    {/* Terms */}
    <section className="mb-8 bg-white border-2 border-gray-200 p-6" data-testid="terms">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><FileText className="w-5 h-5" />Algemene Voorwaarden</h2>
      <div className="text-sm text-gray-700 space-y-3">
        <p><strong>Dienst</strong></p>
        <p className="text-gray-600">BoPeDo biedt een AI-gebaseerd debatplatform. AI-gegenereerde antwoorden zijn informatief en niet juridisch of financieel bindend.</p>
        <p><strong>Gebruik</strong></p>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Geen misbruik van het platform (spam, illegale inhoud)</li>
          <li>AI-output mag niet worden gepresenteerd als professioneel advies</li>
          <li>Het platform is 18+</li>
        </ul>
        <p><strong>Premium Abonnement</strong></p>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Maandelijks opzegbaar</li>
          <li>Geen restitutie voor lopende maand bij opzegging</li>
          <li>Prijs kan wijzigen met 30 dagen vooraankondiging</li>
        </ul>
        <p><strong>Aansprakelijkheid</strong></p>
        <p className="text-gray-600">BoPeDo is niet aansprakelijk voor beslissingen genomen op basis van AI-gegenereerde analyse. Gebruik het platform op eigen risico.</p>
      </div>
    </section>

    {/* FAQ */}
    <section className="mb-8 bg-white border-2 border-gray-200 p-6" data-testid="faq-section">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><HelpCircle className="w-5 h-5" />FAQ</h2>
      <div className="space-y-4 text-sm">
        {[
          ["Wat is Debate Engine?", "Een AI-platform dat meerdere expertperspectieven combineert om complexe vragen te analyseren. Denk aan een raad van deskundigen, beschikbaar in seconden."],
          ["Is het gratis?", "Ja, de basisversie is gratis met 4 AI-debaters per sessie. Premium geeft je meer debaters en functies."],
          ["Hoe betrouwbaar zijn de antwoorden?", "We combineren meerdere AI-providers en perspectieven. Elke conclusie wordt geverifieerd. Gebruik het als startpunt, niet als definitief advies."],
          ["Kan ik mijn data laten verwijderen?", "Ja, stuur een mail naar info@debate-engine.com en we verwijderen je gegevens binnen 72 uur."],
          ["Welke talen worden ondersteund?", "Het platform werkt in alle talen die de onderliggende AI-modellen ondersteunen, inclusief Nederlands en Engels."],
        ].map(([q, a]) => (
          <details key={q}>
            <summary className="font-medium cursor-pointer hover:text-[#002FA7]">{q}</summary>
            <p className="mt-1 text-gray-600 pl-4">{a}</p>
          </details>
        ))}
      </div>
    </section>

    {/* Contact */}
    <section className="bg-white border-2 border-gray-200 p-6" data-testid="contact-section">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Mail className="w-5 h-5" />Contact</h2>
      <div className="text-sm text-gray-700 space-y-2">
        <p>E-mail: <a href="mailto:info@debate-engine.com" className="text-[#002FA7] hover:underline">info@debate-engine.com</a></p>
        <p>Founder: Ronald Lubach</p>
        <p className="text-xs text-gray-500 mt-4">Reactietijd: binnen 48 uur op werkdagen.</p>
      </div>
    </section>

    {/* Trust badges */}
    <div className="mt-8 flex flex-wrap gap-3 justify-center">
      {["AVG/GDPR Compliant", "KvK Geregistreerd", "Geen Dark Patterns", "Transparante AI"].map(badge => (
        <span key={badge} className="px-3 py-1.5 bg-white border border-gray-200 text-[10px] font-mono uppercase text-gray-600">{badge}</span>
      ))}
    </div>
  </div>
);
